import React, { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp, Star, X } from "lucide-react";
import type { DrinkCategory, Drink } from "../../types";
import { drinksApi } from "../../api/drinks";
import { UI_TEXT } from "../../constants/uiText";

interface DrinkOrderModalProps {
  isOpen: boolean;
  eventId: string;
  currentUserName: string;
  onClose: () => void;
  onOrderSuccess: () => void;
}

interface QuantityMap {
  [drinkItemId: string]: number;
}

const DrinkOrderModal: React.FC<DrinkOrderModalProps> = ({
  isOpen,
  eventId,
  currentUserName: _currentUserName,
  onClose,
  onOrderSuccess,
}) => {
  const [menu, setMenu] = useState<DrinkCategory[]>([]);
  const [favorites, setFavorites] = useState<Drink[]>([]);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<QuantityMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchMenu = useCallback(async () => {
    setIsLoading(true);
    try {
      const [data, favs] = await Promise.all([
        drinksApi.getDrinkMenu(eventId),
        drinksApi.getDrinkFavorites(),
      ]);
      setMenu(data);
      setFavorites(favs);
      // 最初のカテゴリをデフォルトで開く
      if (data.length > 0) {
        setOpenCategories(new Set([data[0].id]));
      }
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (isOpen) {
      fetchMenu();
      setQuantities({});
      setFavorites([]);
    }
  }, [isOpen, fetchMenu]);

  if (!isOpen) return null;

  const toggleCategory = (categoryId: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const setQuantity = (drinkItemId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [drinkItemId]: Math.max(0, value) }));
  };

  const additionalCost = menu
    .flatMap((cat) => cat.subCategories.flatMap((sub) => sub.items))
    .reduce((sum, item) => sum + item.price * (quantities[item.id] ?? 0), 0);

  const hasAnyOrder = Object.values(quantities).some((q) => q > 0);

  const handleSubmit = async () => {
    if (!hasAnyOrder) return;
    setIsSubmitting(true);
    try {
      const drinks: Drink[] = [];
      menu.forEach((cat) => {
        cat.subCategories.forEach((sub) => {
          sub.items.forEach((item) => {
            const qty = quantities[item.id] ?? 0;
            if (qty > 0) {
              for (let i = 0; i < qty; i++) {
                drinks.push({
                  category_id: cat.id,
                  category_name: cat.name,
                  sub_category_id: sub.id,
                  sub_category_name: sub.name,
                  menu_id: item.id,
                  menu_name: item.name,
                  price: item.price,
                });
              }
            }
          });
        });
      });
      await drinksApi.orderEventDrinks(eventId, drinks);
      onOrderSuccess();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-white rounded-[28px] overflow-hidden">
      {/* ヘッダー */}
      <div className="bg-slate-900 p-6 text-white relative shrink-0">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
          aria-label={UI_TEXT.drinkOrder.back}
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
        <h3 className="text-xl font-black tracking-tighter uppercase text-center">
          {UI_TEXT.drinkOrder.title}
        </h3>
      </div>

      {/* コンテンツ */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3 scrollbar-hide">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
            {UI_TEXT.common.loading}
          </div>
        ) : (
          <>
            {favorites.length > 0 && (
              <div className="border border-amber-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-amber-50">
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <span className="font-bold text-slate-800 text-sm">
                    {UI_TEXT.drinkOrder.favoritesSection}
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {favorites.map((fav) => {
                    const qty = quantities[fav.menu_id] ?? 0;
                    return (
                      <div
                        key={fav.menu_id}
                        className="flex items-center justify-between px-4 py-3"
                      >
                        <div>
                          <p className="font-medium text-slate-800 text-sm">
                            {fav.menu_name}
                          </p>
                          <p className="text-xs text-slate-400">
                            ¥{fav.price.toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="w-7 h-7 rounded-full border border-slate-300 text-slate-600 font-bold text-lg flex items-center justify-center hover:bg-slate-100 transition-colors disabled:opacity-30"
                            disabled={qty === 0}
                            onClick={() => setQuantity(fav.menu_id, qty - 1)}
                            aria-label="減らす"
                          >
                            −
                          </button>
                          <span className="w-6 text-center font-bold text-slate-800 text-sm">
                            {qty}
                          </span>
                          <button
                            className="w-7 h-7 rounded-full border border-slate-300 text-slate-600 font-bold text-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                            onClick={() => setQuantity(fav.menu_id, qty + 1)}
                            aria-label="増やす"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {menu.map((category) => {
              const isOpen = openCategories.has(category.id);
              return (
                <div
                  key={category.id}
                  className="border border-slate-200 rounded-xl overflow-hidden"
                >
                  {/* カテゴリヘッダー（アコーディオントグル） */}
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="font-bold text-slate-800 text-sm">
                      {category.name}
                    </span>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </button>

                  {/* サブカテゴリ＋ドリンク一覧 */}
                  {isOpen && (
                    <div>
                      {category.subCategories.map((sub) => (
                        <div key={sub.id}>
                          {/* サブカテゴリ名（"-" のときは表示しない） */}
                          {sub.name !== "-" && (
                            <div className="px-4 py-2 bg-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              {sub.name}
                            </div>
                          )}
                          <div className="divide-y divide-slate-100">
                            {sub.items.map((item) => {
                              const qty = quantities[item.id] ?? 0;
                              return (
                                <div
                                  key={item.id}
                                  className="flex items-center justify-between px-4 py-3"
                                >
                                  <div>
                                    <p className="font-medium text-slate-800 text-sm">
                                      {item.name}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      ¥{item.price.toLocaleString()}
                                    </p>
                                  </div>
                                  {/* 数量セレクター */}
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="w-7 h-7 rounded-full border border-slate-300 text-slate-600 font-bold text-lg flex items-center justify-center hover:bg-slate-100 transition-colors disabled:opacity-30"
                                      disabled={qty === 0}
                                      onClick={() =>
                                        setQuantity(item.id, qty - 1)
                                      }
                                      aria-label="減らす"
                                    >
                                      −
                                    </button>
                                    <span className="w-6 text-center font-bold text-slate-800 text-sm">
                                      {qty}
                                    </span>
                                    <button
                                      className="w-7 h-7 rounded-full border border-slate-300 text-slate-600 font-bold text-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                                      onClick={() =>
                                        setQuantity(item.id, qty + 1)
                                      }
                                      aria-label="増やす"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* フッター: 合計と登録ボタン */}
      <div className="shrink-0 px-5 pb-6 pt-3 border-t border-slate-100 bg-white space-y-3">
        {additionalCost > 0 && (
          <div className="flex justify-between items-center text-sm text-slate-500">
            <span>{UI_TEXT.drinkOrder.drinkFee}</span>
            <span className="font-bold text-slate-700">
              ¥{additionalCost.toLocaleString()}
            </span>
          </div>
        )}
        <button
          className="w-full bg-slate-900 text-white py-4 rounded-xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
          disabled={!hasAnyOrder || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting
            ? UI_TEXT.drinkOrder.submitting
            : UI_TEXT.drinkOrder.registerButton}
        </button>
      </div>
    </div>
  );
};

export default DrinkOrderModal;
